import {useAuth0} from "@auth0/auth0-react";

export interface GenerativeModel {
    id: string;
    group_name: string;
    name: string;
    description: string;
    thumbnail_image: string;
    entity_type: "LICENSABLE_PROPERTY" | "USER_PRODUCT" | "BRAND";
    entity_id: string;
    status: string;
    generative_tag: string;
    model_type: string; //lora type
    base_model: string;
    stat_access_count?: number;
    stat_usage_count?: number;
    is_public?: boolean;
    quality_score?: number;
    created_at?: string;
    created_by?: string;
    training_set_id?: string;
    task_queue_id?: string;
}

export interface GenerativeModelVariation {
    id?: string;
    generative_model_id?: string;
    status?: string;
    base_model?: string;
    external_service?: string;
    external_id?: string;
    config?: string;
    task_queue_id?: string;
    is_default?: boolean;
    evaluation_score?: number;
    optional_params?: string;
    created_at?: string;
    updated_at?: string;
}

export function useGenerativeModelAPI() {
    const { getAccessTokenSilently } = useAuth0();

    const getModelsForOrganization = (organizationId: string, activeOnly: boolean = false): Promise<GenerativeModel[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/admin/organization/${organizationId}?activeOnly=${activeOnly}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModel[]) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };
    const getModelsForUser = (userId: string, activeOnly: boolean = false): Promise<GenerativeModel[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/admin/user/${userId}?activeOnly=${activeOnly}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModel[]) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch invitation code: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    };
    const updateModel = (id: string, payload: Partial<GenerativeModel>): Promise<GenerativeModel> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/${id}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModel) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to update generative model: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }
    const updateModelVariation = (id: string, payload: Partial<GenerativeModelVariation>): Promise<GenerativeModelVariation> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/${payload.generative_model_id}/variation/${id}`, {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify(payload),
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModelVariation) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to update generative model variation: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }
    const deleteModel = (id: string): Promise<void> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/${id}`, {
                    method: "DELETE",
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            resolve();
                        } else {
                            reject(new Error("Failed to delete generative model: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }
    const getVariationsForModel = (modelId: string): Promise<GenerativeModelVariation[]> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/${modelId}/variations`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModelVariation[]) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch variations: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }

    const getById = (id: string): Promise<GenerativeModel> => {
        return new Promise((resolve, reject) => {
            getAccessTokenSilently().then((accessToken) => {
                fetch(`${process.env.REACT_APP_API_URL}/generative-model/${id}`, {
                    headers: {
                        authorization: `Bearer ${accessToken}`,
                    },
                })
                    .then((response) => {
                        if (response.status === 200) {
                            response.json().then((data: GenerativeModel) => resolve(data)).catch(reject);
                        } else {
                            reject(new Error("Failed to fetch generative model: " + response.statusText));
                        }
                    })
                    .catch(reject);
            }).catch(reject);
        });
    }

    return {
        getModelsForUser,
        getModelsForOrganization,
        updateModel,
        deleteModel,
        getVariationsForModel,
        updateModelVariation,
        getById
    };
}
